import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";

import {
  orderStatusConstants,
  orderStatusStringConstants,
} from "../constants/orderStatusConstants";
import { orderActions } from "../redux/actions";
import { formatNumber } from "../shared/utility";
import { currencyConstants, customerConstants } from "../constants";

const Orders = ({
  getOrderById,
  orders,
  getOrdersWithParams,
  getOrderStatusesWithCount,
  orderStatuses,
}) => {
  const initialGridState = {
    limit: 10,
    page: 1,
    status_id: orderStatusConstants.AWAITING_FULFILLMENT,
    sort: "id:desc",
  };

  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [action, setAction] = useState(true);
  const [gridState, setGridState] = useState(initialGridState);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  useEffect(() => {
    getOrderStatusesWithCount();
    getOrdersWithParams(gridState);
  }, []);

  const baseColumns = [
    {
      name: "Order Id",
      selector: "id",
      sortable: true,
      width: "200px",
      cell: (row) => row?.id || "—",
    },
    {
      name: "Customer Id",
      selector: "customer_id",
      sortable: true,
      width: "200px",
      cell: (row) => row?.customer_id || customerConstants.GUEST,
    },
    {
      name: "Status",
      selector: "custom_status",
      sortable: true,
      width: "200px",
      cell: (row) => row?.custom_status || "—",
    },
    {
      name: "Total",
      selector: "total_inc_tax",
      sortable: true,
      width: "200px",
      cell: (row) => {
        if (!row?.total_inc_tax) return "—";
        const currencySymbol =
          row.currency_code === currencyConstants.CAD
            ? currencyConstants.CANADIAN_DOLLAR
            : currencyConstants.DOLLAR;
        return `${currencySymbol}${formatNumber(row.total_inc_tax)}`;
      },
    },
    {
      name: "Date",
      selector: "date_created",
      sortable: true,
      width: "300px",
      cell: (row) => row?.date_created || "—",
    },
    {
      name: "External Order Id",
      selector: "external_order_id",
      sortable: true,
      width: "300px",
      cell: (row) => row?.external_order_id || "—",
    },
  ];

  const actionsColumn = {
    name: "Actions",
    width: "200px",
    cell: (row) => (
      <div>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => navigate(`/refund-order/${row.id}`)}
        >
          Refund
        </button>
      </div>
    ),
    allowOverflow: true,
    button: true,
  };

  const columns = [...(action ? [actionsColumn] : []), ...baseColumns];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f5f5f5",
        fontSize: "16px",
        borderBottom: "1px solid #e0e0e0",
      },
    },
    headCells: {
      style: {
        fontWeight: "bold",
        color: "#333",
        fontSize: "14px",
      },
    },
  };

  const filterOrdersByStatus = () => {
    setAction(
      gridState.status_id != orderStatusConstants.PENDING &&
        gridState.status_id != orderStatusConstants.DISPUTED &&
        gridState.status_id != orderStatusConstants.AWAITING_PAYMENT &&
        gridState.status_id != orderStatusConstants.DECLINED &&
        gridState.status_id != orderStatusConstants.REFUNDED &&
        gridState.status_id != orderStatusConstants.INCOMPLETE
    );
    getOrdersWithParams(gridState);
  };

  const filterOrdersByOrderId = () => {
    getOrderById(searchText);
  };

  const handleSort = (column, sortDirection) => {
    // This MUST match the selector in the columns exactly
    const columnKey = column.selector;

    // Use a separate map for backend fields
    let sortField = columnKey;

    const orderBy = `${sortField}:${sortDirection}`;

    // Keep these matching the actual column.selector
    setSortColumn(columnKey);
    setSortDirection(sortDirection);
    const state = { ...gridState, sort: `${orderBy}` };
    setGridState(state);
    getOrdersWithParams(state);
  };

  return (
    <div className="container mt-4">
      <h3>Orders</h3>
      <div className="d-flex gap-2 mb-3 mt-5">
        <select
          className="form-select w-auto"
          value={gridState.status_id}
          onChange={(e) =>
            setGridState({ ...gridState, status_id: e.target.value })
          }
        >
          <option value={orderStatusConstants.AWAITING_FULFILLMENT}>
            {orderStatusStringConstants.AWAITING_FULFILLMENT}
          </option>
          <option value={orderStatusConstants.INCOMPLETE}>
            {orderStatusStringConstants.INCOMPLETE}
          </option>
          <option value={orderStatusConstants.PENDING}>
            {orderStatusStringConstants.PENDING}
          </option>
          <option value={orderStatusConstants.AWAITING_PAYMENT}>
            {orderStatusStringConstants.AWAITING_PAYMENT}
          </option>
          <option value={orderStatusConstants.AWAITING_SHIPMENT}>
            {orderStatusStringConstants.AWAITING_SHIPMENT}
          </option>
          <option value={orderStatusConstants.AWAITING_PICKUP}>
            {orderStatusStringConstants.AWAITING_PICKUP}
          </option>
          <option value={orderStatusConstants.PARTIALLY_SHIPPED}>
            {orderStatusStringConstants.PARTIALLY_SHIPPED}
          </option>
          <option value={orderStatusConstants.COMPLETED}>
            {orderStatusStringConstants.COMPLETED}
          </option>
          <option value={orderStatusConstants.SHIPPED}>
            {orderStatusStringConstants.SHIPPED}
          </option>
          <option value={orderStatusConstants.CANCELLED}>
            {orderStatusStringConstants.CANCELLED}
          </option>
          <option value={orderStatusConstants.DECLINED}>
            {orderStatusStringConstants.DECLINED}
          </option>
          <option value={orderStatusConstants.REFUNDED}>
            {orderStatusStringConstants.REFUNDED}
          </option>
          <option value={orderStatusConstants.DISPUTED}>
            {orderStatusStringConstants.DISPUTED}
          </option>
          <option value={orderStatusConstants.MANUAL_VERIFICATION_REQUIRED}>
            {orderStatusStringConstants.MANUAL_VERIFICATION_REQUIRED}
          </option>
          <option value={orderStatusConstants.PARTIALLY_REFUNDED}>
            {orderStatusStringConstants.PARTIALLY_REFUNDED}
          </option>
        </select>
        <button
          className="btn btn-primary"
          onClick={() => filterOrdersByStatus()}
        >
          Search
        </button>
      </div>
      <div className="d-flex gap-2 mb-3 mt-3">
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search By Order Id..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <button
          className="btn btn-primary"
          onClick={() => filterOrdersByOrderId()}
        >
          Search
        </button>
      </div>
      <DataTable
        columns={columns}
        data={orders}
        highlightOnHover
        responsive
        pagination
        paginationServer
        paginationTotalRows={
          (orderStatuses.find((x) => x.id == gridState.status_id) || {})
            .count || 0
        }
        onChangePage={(page) => {
          const updatedState = { ...gridState, page };
          setGridState(updatedState);
          getOrdersWithParams(updatedState);
        }}
        onChangeRowsPerPage={(newLimit, page) => {
          const updatedState = { ...gridState, limit: newLimit, page };
          setGridState(updatedState);
          getOrdersWithParams(updatedState);
        }}
        sortServer
        onSort={handleSort}
        defaultSortField={sortColumn}
        defaultSortAsc={sortDirection === "desc"}
        customStyles={customStyles}
        className="sticky-header-class"
      />
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  orders: state.Order.ordersData,
  orderStatuses: state.Order.orderStatuses,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getOrderById: (orderId) => dispatch(orderActions.getOrderById(orderId)),
  getOrdersWithParams: (params) =>
    dispatch(orderActions.getOrdersWithParams(params)),
  getOrderStatusesWithCount: () =>
    dispatch(orderActions.getOrderStatusesWithCount()),
});

// Connecting Redux to the component
export default connect(mapStateToProps, mapDispatchToProps)(Orders);
